import React from 'react'
import { Pressable, ScrollView, useWindowDimensions, View, ViewStyle } from 'react-native'

import LeftArrowIcon from '@common/assets/svg/LeftArrowIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

// Shared rounded radius for buttons / inputs across the redesigned auth screens.
export const AUTH_RADIUS = 16
// The centered onboarding card caps at the popup width so the extension popup,
// the mobile-web SPA and native mobile all render the same card.
export const ONBOARDING_CARD_WIDTH = 480
// Below this width the onboarding screens drop the centered floating card and
// render full-screen with a top navigation bar (back button + centered title),
// matching the mobile mocks for the mobile-web SPA, native mobile and the popup.
// Wide browsers (desktop web / extension tab) keep the centered card.
export const ONBOARDING_FULLSCREEN_MAX_WIDTH = 600

type Props = {
  children: React.ReactNode
  // Centered node shown above the title (logo on Welcome, check on success, etc.)
  topContent?: React.ReactNode
  title?: string
  subtitle?: string
  // Renders the circular back button in the top-left corner.
  onBackPress?: () => void
  // When true (and a back button is shown), the title sits on the same row as
  // the back button, vertically centered. Otherwise the title is stacked below.
  headerInline?: boolean
  cardStyle?: ViewStyle
}

const BackButton: React.FC<{ onPress: () => void }> = ({ onPress }) => {
  const { theme } = useTheme()

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }: any) => [
        {
          width: 36,
          height: 36,
          borderRadius: 18,
          backgroundColor: hovered ? theme.tertiaryBackground : theme.secondaryBackground
        },
        flexbox.center
      ]}
    >
      <LeftArrowIcon color={theme.primaryText} width={12} height={12} />
    </Pressable>
  )
}

// Centered dark card shared by every onboarding/auth screen and every render
// target (extension popup/tab, mobile-web SPA, native mobile). Mirrors the
// "Welcome back" UnlockPanel so the whole auth flow looks consistent.
const OnboardingCard: React.FC<Props> = ({
  children,
  topContent,
  title,
  subtitle,
  onBackPress,
  headerInline,
  cardStyle
}) => {
  const { theme } = useTheme()
  const { width: windowWidth } = useWindowDimensions()
  const isFullScreen = windowWidth < ONBOARDING_FULLSCREEN_MAX_WIDTH

  const titleNode = !!title && (
    <Text
      fontSize={20}
      weight="semiBold"
      color={theme.primaryText}
      style={[text.center, subtitle ? spacings.mbTy : spacings.mbLg]}
    >
      {title}
    </Text>
  )

  const inlineHeader = !!onBackPress && !!headerInline

  // Narrow widths (mobile-web on a phone, native mobile, the popup): full-screen
  // layout with the header pinned to the top as a navigation bar. Screens with a
  // back handler get the [back · title · spacer] nav bar and top-aligned content;
  // the Welcome screen (no back) keeps its content vertically centered.
  if (isFullScreen) {
    return (
      <View
        style={[flexbox.flex1, { width: '100%', backgroundColor: theme.onboardingPageBackground }]}
      >
        {!!onBackPress && (
          <View
            style={[
              flexbox.directionRow,
              flexbox.alignCenter,
              spacings.ph,
              { paddingTop: 8, paddingBottom: 12 }
            ]}
          >
            <BackButton onPress={onBackPress} />
            <View style={flexbox.flex1}>
              <Text fontSize={18} weight="semiBold" color={theme.primaryText} style={text.center}>
                {title}
              </Text>
            </View>
            {/* Spacer to keep the title centered against the back button */}
            <View style={{ width: 36 }} />
          </View>
        )}

        <ScrollView
          style={flexbox.flex1}
          contentContainerStyle={[
            { flexGrow: 1, paddingBottom: 24 },
            spacings.ph,
            !onBackPress && [flexbox.justifyCenter, { paddingTop: 24 }]
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {!!topContent && <View style={[flexbox.alignCenter, spacings.mbSm]}>{topContent}</View>}

          {!onBackPress && titleNode}

          {!!subtitle && (
            <Text
              fontSize={16}
              weight="medium"
              color={theme.secondaryText}
              style={[text.center, spacings.mbLg]}
            >
              {subtitle}
            </Text>
          )}

          {children}
        </ScrollView>
      </View>
    )
  }

  return (
    <View style={[flexbox.flex1, flexbox.alignCenter, flexbox.justifyCenter, spacings.ph]}>
      <View
        style={[
          {
            width: Math.min(ONBOARDING_CARD_WIDTH, windowWidth * 0.9),
            maxHeight: '90%',
            backgroundColor: theme.onboardingCardBackground,
            borderRadius: AUTH_RADIUS,
            padding: 24,
            shadowColor: theme.onboardingCardShadow,
            shadowOffset: { width: 0, height: 12 },
            shadowOpacity: 1,
            shadowRadius: 32,
            elevation: 12
          },
          cardStyle
        ]}
      >
        {inlineHeader ? (
          <View style={[flexbox.directionRow, flexbox.alignCenter, spacings.mbLg]}>
            <BackButton onPress={onBackPress} />
            <View style={flexbox.flex1}>
              <Text fontSize={20} weight="semiBold" color={theme.primaryText} style={text.center}>
                {title}
              </Text>
            </View>
            {/* Spacer to keep the title visually centered against the back button */}
            <View style={{ width: 40 }} />
          </View>
        ) : (
          <>
            {!!onBackPress && (
              <View style={spacings.mbMd}>
                <BackButton onPress={onBackPress} />
              </View>
            )}

            {!!topContent && <View style={[flexbox.alignCenter, spacings.mbSm]}>{topContent}</View>}

            {titleNode}
          </>
        )}

        <ScrollView
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          {!!subtitle && (
            <Text
              fontSize={16}
              weight="medium"
              color={theme.secondaryText}
              style={[text.center, spacings.mbLg]}
            >
              {subtitle}
            </Text>
          )}

          {children}
        </ScrollView>
      </View>
    </View>
  )
}

export default React.memo(OnboardingCard)
